import "./BillingPage.scss";
import {useCallback, useEffect, useState} from "react";
import AccountsApi from "../../../api/AccountsApi";
import {useRecoilState} from "recoil";
import {memberState} from "../../../state/atoms";
import BillingHistory from "../billing/BillingHistory";
import {
  BillingAddressDto,
  LightCardDto,
  PurchaseDto,
  UpdateCardDto,
} from "../../../generated/account-client";
import {handleServerError} from "../../../util/ErrorHandler";
import ChangeCardModal from "../modal/ChangeCardModal";
import {Button} from "../misc/Button";
import {isEmpty} from "lodash";
import CardOnFile from "../misc/CardOnFile";
import {Skeleton} from "antd";
import {loadStripe} from "@stripe/stripe-js";
import {env} from "../../../env/env";
import {STRIPE_DEFAULT_SETTINGS} from "../../../util/constants";
import {Elements} from "@stripe/react-stripe-js";

export function BillingPage() {
  const [member] = useRecoilState(memberState);
  const [billingAddress, setBillingAddress] = useState({} as BillingAddressDto);
  const [card, setCard] = useState({} as LightCardDto);
  const [purchases, setPurchases] = useState([] as PurchaseDto[]);
  const [loading, setLoading] = useState(true);
  const [loadingPurchases, setLoadingPurchases] = useState(true);
  const [changePaymentInfoModal, setChangePaymentInfoModal] = useState(false);

  const fetchPurchases = useCallback(() => {
    if (!member.id) return;

    setLoadingPurchases(true);
    AccountsApi.getPurchases(member.id)
      .then((resp) => {
        setLoadingPurchases(false);
        setPurchases(
          resp.data.sort(
            (a, b) => parseInt(b.purchaseDate!) - parseInt(a.purchaseDate!)
          )
        );
      })
      .catch((e) => {
        handleServerError(e);
        setLoadingPurchases(false);
      });
  }, [member.id]);

  const fetchPaymentDetails = useCallback(() => {
    if (!member.id) return;

    setLoading(true);
    AccountsApi.getPaymentDetails(member.id)
      .then((resp) => {
        if (resp.data.card) {
          setCard(resp.data.card);
        }
        if (resp.data.billing) {
          const addr = resp.data.billing;
          if (addr.zip === '0') {
            addr.zip = '';
          }
          setBillingAddress(addr);
        }

        setLoading(false);
      })
      .catch((e) => {
        handleServerError(e);
        setLoading(false);
      });
  }, [member.id]);

  useEffect(fetchPurchases, [fetchPurchases]);
  useEffect(fetchPaymentDetails, [fetchPaymentDetails]);

  function onCloseModal(card: UpdateCardDto, billingInfo: BillingAddressDto) {
    if (!isEmpty(card) && !isEmpty(billingInfo)) {
      const lightCard = {
        ending: String(
          card.card?.number?.substring(
            card.card?.number?.length - 4,
            card.card?.number?.length
          )
        ),
        exprYear: Number(card.card?.year),
        exprMonth: Number(card.card?.month),
      };
      setCard(lightCard);
      setBillingAddress(billingInfo);
    }
    setChangePaymentInfoModal(false);
  }

  return (
    <div className={"content billing-page"}>
      {loading && <Skeleton/>}

      {!loading && (
        <>
          <h3>Payment Information</h3>

          <CardOnFile card={card}/>

          <section>
            <h5>Billed to:</h5>
            <p>
              {billingAddress.first} {billingAddress.last}
            </p>
            <p>{billingAddress.address}</p>
            <p>
              {billingAddress.city ? `${billingAddress.city},` : ""}{" "}
              {billingAddress.state} {billingAddress.zip}
            </p>
          </section>

          {member.owner && <Button
            type={"Primary"}
            onClick={() => setChangePaymentInfoModal(true)}
          >
            Change
          </Button>}

          <BillingHistory purchases={purchases} loading={loadingPurchases}/>

          <Elements stripe={loadStripe(env.stripeKey)} options={STRIPE_DEFAULT_SETTINGS}>
            <ChangeCardModal
              card={card}
              member={member}
              billingAddress={billingAddress}
              visible={changePaymentInfoModal}
              onClose={onCloseModal}
            />
          </Elements>
        </>
      )}
    </div>
  );
}
